import { Injectable } from '@angular/core';
import { ImageUtilitiesService, TFileResourceType } from '@utilities/image-utilities.service';
import { ICustomFile } from './file-uploader.interface';

@Injectable({
  providedIn: 'root',
})
export class FileUploaderService {
  private static readonly imageFileExtensions = ['gif', 'jpeg', 'jpg', 'png', 'webp'] as const;

  // Used only as the placeholder File's own type in createCustomFile below - never inspected to
  // decide what the *real* remote file is (that's not knowable synchronously anymore, see
  // createCustomFile's comment).
  private static readonly placeholderMimeType = 'image/png';
  private static readonly placeholderFileName = 'file.png';

  // A remote file/image id used to be a base64-encoded storage path with the original name and
  // extension embedded in it (see git history), so a name/extension/mime-type could always be
  // read back out of the id synchronously. Ids are now opaque backend-issued UUIDs (the `files`
  // table's primary key) with no encoded information at all - name/mime-type/size now only ever
  // come from the backend, either via the file read endpoint's response headers
  // (ImageUtilitiesService.getFileMetadata/getImageUrlAndSize/getFileUrlAndSize, Content-Disposition
  // + Content-Type + Content-Length) or, for social content file attachments, directly on the list
  // response (see SocialContentFilesService.mapSocialContentFile). There is deliberately no
  // synchronous id -> name/type helper here anymore - every caller that used to decode one now goes
  // through one of those two async paths instead.

  // Builds a placeholder File standing in for a remote file this component already has an id for
  // (e.g. re-opening a campaign form with a previously-uploaded visual/showcase/product-placement
  // image) so form validators that operate on a File object (imageExtensionValidator,
  // fileExtensionValidator) see something well-formed and the field reads as populated, without
  // downloading the real bytes just to satisfy them. Every current call site only ever attaches
  // this to an image-only field, so a fixed image/png placeholder is always valid; the real
  // name/mime-type get displayed once file-uploader.component.ts's async metadata fetch resolves
  // (see its fileName()/currentFileType() computed signals), which is what the user actually sees -
  // this placeholder's own name is never rendered.
  public static createCustomFile(fileId: string): ICustomFile | null {
    if (!ImageUtilitiesService.isDisplayableFileId(fileId)) {
      return null;
    }

    const blob = new Blob([], { type: this.placeholderMimeType });
    const file = new File([blob], this.placeholderFileName, { type: this.placeholderMimeType }) as ICustomFile;

    file.isMockFile = true;

    return file;
  }

  public static getFileTypeByExtension(extension?: string | null): TFileResourceType {
    if (!extension) {
      return 'file';
    }

    const normalizedExtension = extension.toLowerCase();

    if (this.imageFileExtensions.includes(normalizedExtension as (typeof this.imageFileExtensions)[number])) {
      return 'image';
    }

    return 'file';
  }

  public static getFileTypeByFile(file?: File | null): TFileResourceType {
    const extension = file?.name?.split('.')?.pop()?.toLowerCase() ?? null;

    return this.getFileTypeByExtension(extension);
  }

  // Companion to getFileTypeByFile/getFileTypeByExtension above (both extension-based, for a real
  // local File the user just picked) - this one is for a remote attachment where all we have is
  // the mime-type the backend reported (Content-Type header, or the social-content-files list
  // response's mimeType field), no file name/extension.
  public static getFileTypeByMimeType(mimeType?: string | null): TFileResourceType {
    return mimeType?.toLowerCase()?.startsWith('image/') ? 'image' : 'file';
  }

  public static formatBytes(bytes: number): string {
    const kilobyte = 1000; // 1000 (KB) instead of 1024 (KiB) to match the behavior of the browser

    if (bytes < kilobyte) {
      return `${bytes} B`;
    }

    const units = ['KB', 'MB', 'GB', 'TB'];
    let unitIndex = -1;
    let value = bytes;

    do {
      value = value / kilobyte;
      unitIndex++;
    } while (value >= kilobyte && unitIndex < units.length - 1);

    const rounded = Math.round(value * 10) / 10;

    return `${rounded} ${units[unitIndex]}`;
  }
}
